import React from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  SimpleGrid,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Badge,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const LineChartTracker = () => {
  const data = [
    { name: "Birth", shortName: "0m", months: 0, height: 48, weight: 3160 },
    { name: "1.5 months", shortName: "1.5m", months: 1.5, height: 54, weight: 4300 },
    { name: "2.5 months", shortName: "2.5m", months: 2.5, height: 58, weight: 5300 },
    { name: "3.2 months", shortName: "3.2m", months: 3.2, height: 60, weight: 5730 },
    { name: "4 months", shortName: "4m", months: 4, height: 62, weight: 6170 },
    { name: "4.3 months", shortName: "4.3m", months: 4.3, height: 62, weight: 6550 },
    { name: "5 months", shortName: "5m", months: 5, height: 65, weight: 6998 },
    { name: "6 months", shortName: "6m", months: 6, height: 67, weight: 7250 },
    { name: "6.7 months", shortName: "6.7m", months: 6.7, height: 68.5, weight: 7870 },
    { name: "7 months", shortName: "7m", months: 7, height: 68.5, weight: 7900 },
    { name: "8 months", shortName: "8m", months: 8, height: 69.5, weight: 8450 },
    { name: "10 months", shortName: "10m", months: 10, height: 73, weight: 9350 },
    { name: "11 months", shortName: "11m", months: 11, height: 73, weight: 9350 },
    { name: "12 months", shortName: "12m", months: 12, height: 75.5, weight: 9500 },
    { name: "1 year 4.6 months", shortName: "1y4.6m", months: 16.6, height: 78, weight: 10700 },
  ];

  const latestData = data[data.length - 1];
  const previousData = data[data.length - 2];
  const firstData = data[0];

  const heightGrowth = latestData.height - previousData.height;
  const weightGrowth = latestData.weight - previousData.weight;
  const totalHeightGrowth = latestData.height - firstData.height;
  const totalWeightGrowth = latestData.weight - firstData.weight;

  // BMI calculation (weight in kg / height in m^2)
  const bmi = (
    (latestData.weight / 1000) /
    Math.pow(latestData.height / 100, 2)
  ).toFixed(1);

  // Average monthly growth rate
  const avgMonthlyHeight = (totalHeightGrowth / latestData.months).toFixed(1);
  const avgMonthlyWeight = (totalWeightGrowth / latestData.months / 1000).toFixed(2);

  // Responsive values
  const chartHeight = useBreakpointValue({ base: 220, sm: 280, md: 350 });
  const xAxisKey = useBreakpointValue({ base: "shortName", md: "name" });
  const showYAxisLabel = useBreakpointValue({ base: false, md: true });
  const chartMargin = useBreakpointValue({
    base: { top: 5, right: 5, left: -15, bottom: 5 },
    md: { top: 10, right: 30, left: 0, bottom: 10 },
  });
  const xAxisAngle = useBreakpointValue({ base: -45, md: -15 });
  const xAxisHeight = useBreakpointValue({ base: 45, md: 60 });
  const dotSize = useBreakpointValue({ base: 3, md: 5 });
  const strokeWidth = useBreakpointValue({ base: 2, md: 3 });

  // Custom Tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Find full name from data
      const point = data.find(
        (d) => d.name === label || d.shortName === label
      );
      const displayLabel = point?.name || label;

      return (
        <Box
          bg="gray.800"
          p={3}
          borderRadius="lg"
          borderWidth="1px"
          borderColor="teal.500"
          boxShadow="0 0 20px rgba(56, 178, 172, 0.3)"
          maxW="200px"
        >
          <Text fontSize="sm" fontWeight="bold" color="white" mb={2}>
            {displayLabel}
          </Text>
          {payload.map((entry, index) => (
            <HStack key={index} spacing={2}>
              <Box w={3} h={3} bg={entry.color} borderRadius="full" flexShrink={0} />
              <Text fontSize="xs" color="gray.300">
                {entry.name}:{" "}
                <Text as="span" fontWeight="bold">
                  {entry.value}
                </Text>
                {entry.name.includes("Height") ? " cm" : " g"}
              </Text>
            </HStack>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <VStack spacing={{ base: 4, md: 6 }} w="full" align="stretch">
      {/* Header */}
      <VStack spacing={1}>
        <Heading
          size={{ base: "sm", md: "md" }}
          textAlign="center"
          color="teal.300"
          fontWeight="bold"
        >
          📈 Growth Tracker
        </Heading>
        <Text fontSize="xs" color="gray.400" textAlign="center">
          Mattias' height and weight progress over time
        </Text>
      </VStack>

      {/* Statistics Cards — top row: current values */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 2, md: 4 }}>
        <Card
          bg="rgba(45, 55, 72, 0.7)"
          borderWidth="1px"
          borderColor="purple.700"
          backdropFilter="blur(10px)"
        >
          <CardBody p={{ base: 2.5, md: 3 }}>
            <Stat>
              <StatLabel color="gray.400" fontSize="xs">
                <Text as="span" display={{ base: "none", md: "inline" }}>
                  Current Height
                </Text>
                <Text as="span" display={{ base: "inline", md: "none" }}>
                  Height
                </Text>
              </StatLabel>
              <StatNumber color="purple.300" fontSize={{ base: "lg", md: "xl" }}>
                {latestData.height} cm
              </StatNumber>
              <StatHelpText fontSize="xs" color="teal.300" mb={0}>
                <StatArrow type={heightGrowth >= 0 ? "increase" : "decrease"} />
                {heightGrowth} cm
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card
          bg="rgba(45, 55, 72, 0.7)"
          borderWidth="1px"
          borderColor="green.700"
          backdropFilter="blur(10px)"
        >
          <CardBody p={{ base: 2.5, md: 3 }}>
            <Stat>
              <StatLabel color="gray.400" fontSize="xs">
                <Text as="span" display={{ base: "none", md: "inline" }}>
                  Current Weight
                </Text>
                <Text as="span" display={{ base: "inline", md: "none" }}>
                  Weight
                </Text>
              </StatLabel>
              <StatNumber color="green.300" fontSize={{ base: "lg", md: "xl" }}>
                {(latestData.weight / 1000).toFixed(2)} kg
              </StatNumber>
              <StatHelpText fontSize="xs" color="teal.300" mb={0}>
                <StatArrow type={weightGrowth >= 0 ? "increase" : "decrease"} />
                {weightGrowth} g
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card
          bg="rgba(45, 55, 72, 0.7)"
          borderWidth="1px"
          borderColor="teal.700"
          backdropFilter="blur(10px)"
        >
          <CardBody p={{ base: 2.5, md: 3 }}>
            <Stat>
              <StatLabel color="gray.400" fontSize="xs">
                <Text as="span" display={{ base: "none", md: "inline" }}>
                  Total Growth
                </Text>
                <Text as="span" display={{ base: "inline", md: "none" }}>
                  Total ↕
                </Text>
              </StatLabel>
              <StatNumber color="teal.300" fontSize={{ base: "lg", md: "xl" }}>
                +{totalHeightGrowth} cm
              </StatNumber>
              <StatHelpText fontSize="xs" color="gray.500" mb={0}>
                ~{avgMonthlyHeight} cm/month
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card
          bg="rgba(45, 55, 72, 0.7)"
          borderWidth="1px"
          borderColor="cyan.700"
          backdropFilter="blur(10px)"
        >
          <CardBody p={{ base: 2.5, md: 3 }}>
            <Stat>
              <StatLabel color="gray.400" fontSize="xs">
                <Text as="span" display={{ base: "none", md: "inline" }}>
                  Weight Gain
                </Text>
                <Text as="span" display={{ base: "inline", md: "none" }}>
                  Gain ⚖️
                </Text>
              </StatLabel>
              <StatNumber color="cyan.300" fontSize={{ base: "lg", md: "xl" }}>
                +{(totalWeightGrowth / 1000).toFixed(2)} kg
              </StatNumber>
              <StatHelpText fontSize="xs" color="gray.500" mb={0}>
                ~{avgMonthlyWeight} kg/month
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Chart Card */}
      <Card
        bg="rgba(45, 55, 72, 0.7)"
        borderWidth="2px"
        borderColor="teal.700"
        backdropFilter="blur(10px)"
        boxShadow="0 0 30px rgba(56, 178, 172, 0.2)"
      >
        <CardBody p={{ base: 2, sm: 3, md: 4 }}>
          <VStack spacing={{ base: 2, md: 4 }} align="stretch">
            {/* Chart Header */}
            <HStack justify="space-between" flexWrap="wrap" gap={2}>
              <Text
                fontSize={{ base: "xs", md: "sm" }}
                color="gray.300"
                fontWeight="semibold"
              >
                Growth Timeline
              </Text>
              <HStack spacing={3}>
                <HStack spacing={1}>
                  <Box w={3} h={3} bg="purple.400" borderRadius="full" />
                  <Text fontSize="xs" color="gray.400">
                    Height
                  </Text>
                </HStack>
                <HStack spacing={1}>
                  <Box w={3} h={3} bg="green.400" borderRadius="full" />
                  <Text fontSize="xs" color="gray.400">
                    Weight
                  </Text>
                </HStack>
              </HStack>
            </HStack>

            {/* Chart — responsive height and labels */}
            <ResponsiveContainer width="100%" height={chartHeight}>
              <LineChart data={data} margin={chartMargin}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255, 255, 255, 0.1)"
                  vertical={false}
                />
                <XAxis
                  dataKey={xAxisKey}
                  tick={{ fontSize: 10, fill: "#A0AEC0" }}
                  stroke="rgba(255, 255, 255, 0.2)"
                  tickLine={false}
                  angle={xAxisAngle}
                  textAnchor="end"
                  height={xAxisHeight}
                  interval={0}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 10, fill: "#A0AEC0" }}
                  stroke="rgba(255, 255, 255, 0.2)"
                  tickLine={false}
                  width={35}
                  label={
                    showYAxisLabel
                      ? {
                          value: "Height (cm)",
                          angle: -90,
                          position: "insideLeft",
                          style: {
                            fill: "#B794F4",
                            fontSize: 12,
                            fontWeight: "bold",
                          },
                        }
                      : undefined
                  }
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 10, fill: "#A0AEC0" }}
                  stroke="rgba(255, 255, 255, 0.2)"
                  tickLine={false}
                  width={40}
                  label={
                    showYAxisLabel
                      ? {
                          value: "Weight (g)",
                          angle: -90,
                          position: "insideRight",
                          style: {
                            fill: "#48BB78",
                            fontSize: 12,
                            fontWeight: "bold",
                          },
                        }
                      : undefined
                  }
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="height"
                  stroke="#B794F4"
                  strokeWidth={strokeWidth}
                  dot={{
                    r: dotSize,
                    fill: "#B794F4",
                    strokeWidth: 2,
                    stroke: "#1A202C",
                  }}
                  activeDot={{
                    r: dotSize + 3,
                    fill: "#B794F4",
                    stroke: "#fff",
                    strokeWidth: 2,
                  }}
                  name="Height"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="weight"
                  stroke="#48BB78"
                  strokeWidth={strokeWidth}
                  dot={{
                    r: dotSize,
                    fill: "#48BB78",
                    strokeWidth: 2,
                    stroke: "#1A202C",
                  }}
                  activeDot={{
                    r: dotSize + 3,
                    fill: "#48BB78",
                    stroke: "#fff",
                    strokeWidth: 2,
                  }}
                  name="Weight"
                />
              </LineChart>
            </ResponsiveContainer>

            {/* Chart Footer */}
            <HStack
              justify="center"
              pt={{ base: 1, md: 2 }}
              flexWrap="wrap"
              gap={2}
            >
              <Badge colorScheme="teal" fontSize="xs" px={2} py={1}>
                📊 {data.length} measurements
              </Badge>
              <Badge colorScheme="purple" fontSize="xs" px={2} py={1}>
                👶 {latestData.name}
              </Badge>
              <Badge colorScheme="orange" fontSize="xs" px={2} py={1}>
                📐 BMI: {bmi}
              </Badge>
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  );
};

export default LineChartTracker;